/**
 * Shown when the backend could not be reached but the request itself may be perfectly fine,
 * so the customer is told to come back rather than that their link is dead.
 */
export const REQUEST_UNAVAILABLE_MESSAGE = 'We are unable to proceed at this time. Please try again later.';

export const constants = {
  ENCRYPTION_KEY: 'WmZq4t7w!z%C&F)J@NcRfUjXn2r5u8x/',
  LOOKUP_FORM_ID: 'LOOKUP_FORM_ID',
  FA_CREATE_FORM_INFO: 'FA_CREATE_FORM_INFO',
  SENTRY_DSN: 'https://1a671b21f104475eaf6d996c370721a1@o923818.ingest.sentry.io/4504657764614144',
  SENTRY_PROD_DSN: 'https://6632fec9198f4280ad9f9aa41866a8c0@o923818.ingest.sentry.io/4504657967972352',
  WORKFLOW_ID: 'WORKFLOW_ID',
};

// Polling for the Flinks aggregation result after a bank account is linked
export const FinancialAccountAggregationPolling = {
  INTERVAL_MS: 5000,
  MAX_ATTEMPTS: 36, // give up after ~3 minutes
};

// Polling for IDV provider (Persona) metadata to resolve, and for the workflow to advance after SDK completion
export const IdvProviderPolling = {
  INTERVAL_MS: 5000,
  MAX_ATTEMPTS: 36, // give up after ~3 minutes
};

// Polling the sign-here step's async completion status after the signer finishes in the embedded form
export const SignHereCompletionPolling = {
  INTERVAL_MS: 1000,
  MAX_ATTEMPTS: 120, // give up after ~2 minutes
};

// Polling for other required parties to finish signing once the primary has completed their own
// signature. Deliberately much slower than SignHereCompletionPolling and uncapped - this can
// legitimately take hours/days, and the backend also reconciles asynchronously and notifies the
// customer by email/SMS, so there's no need to time out this poll client-side.
export const SignHerePartyProgressPolling = {
  INTERVAL_MS: 30000,
  /** Failed polls in a row before the page reports it: at the interval above, a connection down for ~2.5 minutes. */
  MAX_CONSECUTIVE_FAILURES: 5,
};

export const LinxWorkFlowSteps = {
  CUSTOMER_ACCOUNT: 'customer-account',
  ENHANCE_PROFILE: 'enhance-profile',
  FINANCIAL_ACCOUNT: 'financial-account',
  MOVE_MONEY: 'move-money',
};

export const LinxStatus = {
  ACTIVE: 'ACTIVE',
  CREATED: 'CREATED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
  DECLINED: 'DECLINED',
  REQUEST_DECLINED: 'REQUEST_DECLINED',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  SYSTEM_MAINTENANCE: 'SYSTEM MAINTENANCE',
};

export enum LinxStep {
  Notification = 'notification',
  DocumentAcceptance = 'document-acceptance',
  MoveMoney = 'move-money',
  CollectDocuments = 'collect-documents',
  CreateFinancialAccount = 'financial-account',
  SelectOrCreateFinancialAccount = 'select-or-create-financial-account',
  LinkOrCreateFinancialAccount = 'link-or-create-financial-account',
  LookupOrVerifyFinancialAccount = 'lookup-or-verify-financial-account',
  SignHere = 'sign-here',
  PersonaVerification = 'submit-idv-provider-data',
  SelectCountry = 'select-country',

  //Individual
  CreateIndividualAccount = 'customer-account',
  EnhanceIndividualProfile = 'enhance-profile',
  SubmitIndividualVerification = 'submit-kyc',
  // Business
  CreateBusinessAccount = 'create-business-customer-account',
  EnhanceBusinessProfile = 'enhance-business-customer-account',
  SubmitBusinessVerification = 'submit-kyb',
  // Beneficiary
  CreateBeneficiary = 'create-beneficiary',
  EnhanceBeneficiaryProfile = 'enhance-beneficiary',
  LookupBeneficiary = 'lookup-beneficiary',
  // Counterparty
  CreateCounterparty = 'create-counterparty',
  EnhanceCounterparty = 'enhance-counterparty',

  // Debit Authorization
  LookupOrCreateDebitAuthorization = 'lookup-or-create-debit-authorization',
  CreateDebitAuthorization = 'create-debit-authorization',
}

// NOTE: These lists are used solely to discriminate customer type in
// WorkflowService.getCustomerType(). PersonaVerification ('submit-idv-provider-data')
// is intentionally excluded from both: it is a shared IDV step present in both
// individual and business workflows, so including it makes a business workflow match
// the individual list first and resolve to the wrong customer type (KOR routing bug).
export const LinxIndividualCustomerSteps = [
  LinxStep.CreateIndividualAccount,
  LinxStep.EnhanceIndividualProfile,
  LinxStep.SubmitIndividualVerification,
];

export const LinxBusinessCustomerSteps = [
  LinxStep.CreateBusinessAccount,
  LinxStep.EnhanceBusinessProfile,
  LinxStep.CreateBeneficiary,
  LinxStep.EnhanceBeneficiaryProfile,
  LinxStep.SubmitBusinessVerification,
];

export const LinxCounterpartySteps = [LinxStep.CreateCounterparty, LinxStep.EnhanceCounterparty];

export const IndividualCustomerDocumentTypes = [
  { label: 'Upload Passport', value: 'passport', icon: 'passport', uploaded: false },
  { label: 'Upload Driver’s License', value: 'driving-license', icon: 'driver-license', uploaded: false, twoSided: true },
  { label: 'Other', value: 'other', icon: 'document', uploaded: false },
];

export const BusinessCustomerDocumentTypes = [
  { label: 'Upload Formation', value: 'formation', icon: 'company', uploaded: false },
  { label: 'Upload Tax', value: 'tax', icon: 'accounting', uploaded: false },
  { label: 'Upload Ownership', value: 'ownership', icon: 'businessman', uploaded: false },
  { label: 'Other', value: 'other', icon: 'document', uploaded: false },
];

export enum LinxEnhanceAccountType {
  Individual = 'INDIVIDUAL_CUSTOMER',
  Business = 'BUSINESS_CUSTOMER',
  Benificiary = 'BENEFICIARY',
}

export const IdentityDocumentsNames: Record<string, string> = {
  formation: 'Formation',
  tax: 'Tax',
  ownership: 'Ownership',
  'driving-license': 'Driving License',
  passport: 'Passport',
  other: 'Other',
};
